// The workspace shell's RAW design-system SOURCE — the single definition both
// the shell's own boot (`context.js`) and the extension-iframe render path
// (`functions/index.js`) read from.
//
// WHY THIS MODULE EXISTS — the framework's design-system input contract
// ---------------------------------------------------------------------------
// `create()` / `prepareDesignSystem` treat `context.designSystem` as a RAW
// SOURCE and return a PROCESSED scratch CONFIG:
//
//   raw source                       processed CONFIG
//   color:  { black: 'black' }  ->   color:  { black: { value, var, rgb } }
//   theme:  { document: { '@dark': { background: 'gray1+6' } } }
//                               ->   theme:  { document: { background, color } }
//   icons:  { plus: '<svg …>' } ->   icons:  { plus: '<symbol …>' }
//
// `prepareDesignSystem` deep-clones its input, `deepMerge`s the RAW
// `DEFAULT_CONFIG` into it, and runs `init()` over the result. Feeding it a
// config that has ALREADY been through `init()` therefore re-derives every
// suffix from an already-derived value: `--color-black` becomes
// `--color-black-var`, `--theme-document-background` becomes
// `--theme-document-background-background`, and the base names every themed
// class actually references are never emitted at all. `--theme-document-
// background` then resolves to `var(--theme-document-<scheme>-background)`,
// which nothing defines. `injectSprites` double-converts the same way —
// `convertSvgToSymbol` over an already-converted `<symbol>` string.
//
// The workspace extension pane used to hand `ctx.designSystem` (the shell's
// live, already-init'ed CONFIG) back into `create()` and hit exactly that.
// Every other embed caller — `embedComponent`, the canvas frame-mode tile,
// preview's `renderProject` — passes a project's raw published design system
// and resolves correctly. So the input shape was the whole difference, and the
// fix belongs here, at the caller, not in the transformers: making `init()`
// idempotent would mean teaching every transformer to recognise its own output,
// on an object `prepareDesignSystem` has already blended with RAW
// `DEFAULT_CONFIG` keys — a per-key raw/processed mix no shape check can
// disambiguate reliably. Keeping the input type single-valued is what makes
// `init()` a well-defined raw -> processed function.
//
// This module is a pure data helper. It is deliberately NOT re-exported from
// `functions/index.js` (same as `extensionDataSeed.js`) — it is not an
// `el.call`-able project function.

// Sourced directly from @symbo.ls/utils (NOT the 'smbls' umbrella barrel):
// `deepClone`/`deepDefaults` are plain object helpers that live in
// @symbo.ls/utils/{object,sharedLibraries}.js and are simply re-exported by
// smbls's `export * from '@symbo.ls/utils'` — identical functions, same
// module. `smbls`'s own dist entry rolls up create/element/signal/css/state/
// scratch/default-config/wasm into ONE non-splittable bundle; this file is
// statically reachable from the shell entry (context.js -> here), so an
// `import … from 'smbls'` here pulls that whole framework bundle into the
// eager chunk for two tiny object helpers. @symbo.ls/utils has zero deps and
// ships real per-file dist output, so Parcel tree-shakes it down to just the
// two source files these helpers live in. See
// functions/workspaceProjects.js for the same established pattern.
import { deepClone, deepDefaults } from '@symbo.ls/utils'
import shellDesignSystem from '../designSystem/index.js'
import brandDesignSystem from '@symbo-ls/brand/designSystem'
import marketplaceIntegrationIcons from '../../marketplace/designSystem/integrationIcons.js'

// The shell's own tokens (icons, colors, fonts, spacing, …) as `context.js`
// hands them to `create()`. Marketplace runs standalone (own
// context.designSystem) AND inside this shell — its components/functions are
// merged into the shell context, but it is NOT a shared library, so its
// designSystem is not auto-merged. Fold the integration / registrar brand
// logos into the shell's icon set here so IntegrationLogo's Icon resolves them
// via scratch's sprite on this surface too.
export const workspaceDesignSystemSource = {
  ...shellDesignSystem,
  icons: { ...shellDesignSystem.icons, ...marketplaceIntegrationIcons }
}

// The raw design system for a WORKSPACE-EXTENSION iframe app.
//
// Same tokens the shell renders with, still unprocessed — `create()` inside the
// iframe processes them exactly once, against the iframe's own document, via
// `createConfig(key, ds, { cleanBase: true })` (smbls/src/prepare.js). Brand is
// folded in with `deepDefaults` because the shell gets brand's design system
// the same way at boot: `prepareSharedLibs` -> `mergeSharedLibraries` runs
// `deepDefaults(context.designSystem, sharedLib.designSystem)`, and `brand` is
// the only entry in `sharedLibraries.js` that carries one. Without it an
// extension would resolve fewer tokens than the surrounding shell.
//
// A fresh object every call, never a shared reference: an iframe app's boot
// mutates the config it is handed (`prepareDesignSystem` writes `.document`,
// `createIframeApp` writes `.themeRoot`), and a design system reachable from
// two apps at once is what wedged the shell into a permanent MainLoader in the
// 2026-07-13 regression. Note the raw source carries NEITHER `.themeRoot` nor
// `.document` to begin with — the hazard is now absent by construction rather
// than stripped after the fact.
export const extensionDesignSystemSource = () => {
  const ds = deepClone(workspaceDesignSystemSource)
  deepDefaults(ds, deepClone(brandDesignSystem))
  return ds
}
